import React from "react";
import Checkbox from "@mui/material/Checkbox";
import { mapContactLinkedUserFields } from "../../../../auth/utils/contactLink";

function linkedUserNameWithoutEmail(row) {
  const name = row.linked_user_name?.trim();
  const email = row.linked_user_email?.trim();
  const username = row.linked_user_username?.trim();
  if (name && name !== email) return name;
  if (username) return username;
  const linkedId = row.contact?.linked_user?.id;
  return linkedId != null ? `User #${linkedId}` : "";
}

function LinkedUserColumnContent({ row }) {
  const displayName = linkedUserNameWithoutEmail(row);
  const role = row.linked_user_role?.trim();

  if (!displayName && !role) {
    return <span className="team-members-linked-user-empty">—</span>;
  }

  const title = [displayName, role].filter(Boolean).join(" · ");

  return (
    <div className="team-members-linked-user" title={title}>
      {displayName ? <span className="team-members-linked-user-name">{displayName}</span> : null}
      {role ? <span className="team-members-linked-user-role">{role}</span> : null}
    </div>
  );
}

export function mapContactTeamMemberRow(contact, contactCompanyLabel) {
  const name = `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || "—";
  return {
    id: contact.id,
    contact,
    name,
    company: contactCompanyLabel(contact) || "—",
    email: contact.email?.trim() || "—",
    ...mapContactLinkedUserFields(contact),
  };
}

export function mapCompanyTeamMemberRow(company, companyCategoryLabel) {
  return {
    id: company.id,
    company,
    name: company.name?.trim() || "—",
    category: companyCategoryLabel(company) || "—",
    email: company.email?.trim() || "—",
  };
}

export function buildContactTeamMemberColumns({
  isAdminRole: showLinkedUserColumn,
  assignedContactIds,
  canAssignContacts,
  canUnassignContacts,
  onAssign,
  onUnassign,
}) {
  const columns = [
    {
      key: "name",
      name: "Name",
      thClassName: "team-members-col-name",
      tdClassName: "team-members-table-cell-ellipsis team-members-col-name",
      getCellTitle: (row) => row.name,
    },
    {
      key: "company",
      name: "Company Name",
      thClassName: "team-members-col-company",
      tdClassName: "team-members-table-cell-ellipsis team-members-col-company",
      getCellTitle: (row) => row.company,
    },
    {
      key: "email",
      name: "Email",
      thClassName: "team-members-col-email",
      tdClassName: "team-members-table-cell-ellipsis team-members-table-cell-email team-members-col-email",
      getCellTitle: (row) => row.email,
    },
  ];

  if (showLinkedUserColumn) {
    columns.push({
      key: "linked_user",
      name: "Linked User",
      thClassName: "team-members-col-linked-user",
      tdClassName: "team-members-col-linked-user",
      formatter: (row) => <LinkedUserColumnContent row={row} />,
    });
  }

  columns.push({
    key: "on_project",
    name: "On project",
    thClassName: "team-members-table-col-assign team-members-col-assign",
    tdClassName: "team-members-table-col-assign team-members-col-assign",
    formatter: (row) => {
      const isAssigned = assignedContactIds.has(row.id);
      const canToggle = isAssigned ? canUnassignContacts : canAssignContacts;
      return (
        <Checkbox
          size="small"
          checked={isAssigned}
          disabled={!canToggle}
          onChange={(e) => {
            if (e.target.checked) {
              onAssign(row.id);
            } else {
              onUnassign(row.id);
            }
          }}
          inputProps={{
            "aria-label": `${isAssigned ? "Remove" : "Add"} ${row.name} on project`,
          }}
          sx={{ p: 0.25 }}
        />
      );
    },
  });

  return columns;
}

export function buildCompanyTeamMemberColumns({ assignedCompanyIds, canAssignCompanies, canUnassignCompanies, onAssign, onUnassign }) {
  return [
    {
      key: "name",
      name: "Name",
      thClassName: "team-members-col-name",
      tdClassName: "team-members-table-cell-ellipsis team-members-col-name",
      getCellTitle: (row) => row.name,
    },
    {
      key: "category",
      name: "Category",
      thClassName: "team-members-col-category",
      tdClassName: "team-members-table-cell-ellipsis team-members-col-category",
      getCellTitle: (row) => row.category,
    },
    {
      key: "email",
      name: "Email",
      thClassName: "team-members-col-email",
      tdClassName: "team-members-table-cell-ellipsis team-members-table-cell-email team-members-col-email",
      getCellTitle: (row) => row.email,
    },
    {
      key: "on_project",
      name: "On project",
      thClassName: "team-members-table-col-assign team-members-col-assign",
      tdClassName: "team-members-table-col-assign team-members-col-assign",
      formatter: (row) => {
        const isAssigned = assignedCompanyIds.has(row.id);
        const canToggle = isAssigned ? canUnassignCompanies : canAssignCompanies;
        return (
          <Checkbox
            size="small"
            checked={isAssigned}
            disabled={!canToggle}
            onChange={(e) => {
              if (e.target.checked) {
                onAssign(row.id);
              } else {
                onUnassign(row.id);
              }
            }}
            inputProps={{
              "aria-label": `${isAssigned ? "Remove" : "Add"} ${row.name} on project`,
            }}
            sx={{ p: 0.25 }}
          />
        );
      },
    },
  ];
}
